import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import { AlertComponent } from '@vectronic/ui';
import { SnackbarService } from '@vectronic/util';
import { Subscription } from 'rxjs';
import {
  downloadScheduleJsonFile,
  importScheduleJsonFromInputEvent,
} from '../../helpers/schedule-json.helper';
import { AbstractScheduleService } from '../../services/abstract-schedule.service';
import { RemoteCommandsService } from '../../services/commands/remote-commands.service';
import { GpsXFileImportService } from '../../services/gps-x-file-import.service';
import { ScheduleTemplatesProviderService } from '../../services/schedule-templates-provider.service';
import { ScheduleServices } from '../../services/schedule.service';
import { CalendarComponent } from '../calendar/calendar.component';
import { RuleComponent } from '../rule/rule.component';
import { ScheduleTemplateSelectorComponent } from '../schedule-template-selector/schedule-template-selector.component';
import { GENERIC_SCHEDULE_STRINGS } from './generic-schedule.strings';

@Component({
  selector: 'rc-generic-schedule',
  templateUrl: './generic-schedule.component.html',
  styleUrls: ['./generic-schedule.component.scss'],
  imports: [
    NgClass,
    MatIconButton,
    MatIcon,
    MatTooltip,
    RuleComponent,
    MatButton,
    CalendarComponent,
    AsyncPipe,
    MatDivider,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ScheduleTemplateSelectorComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    AlertComponent,
    FormsModule,
  ],
})
export class GenericScheduleComponent implements OnInit, OnDestroy, AfterContentChecked {
  readonly strings = GENERIC_SCHEDULE_STRINGS;

  private cdref = inject(ChangeDetectorRef);
  private scheduleServices = inject(ScheduleServices);
  gpsXFileImportService = inject(GpsXFileImportService);
  private templatesProvider = inject(ScheduleTemplatesProviderService, { optional: true });
  private snackbarService = inject(SnackbarService);
  remoteCommandsService = inject(RemoteCommandsService);

  title = input<string>('Schedule');
  addScheduleText = input<string>('Add Schedule');
  scheduleType = input.required<dsEnums.ScheduleType>();
  isDisabledByScheduleResponse = input<boolean>(false);
  allowedCollars = input<number[] | undefined>();
  showReset = input<boolean>();
  hideChangedSchedules = input<boolean>();
  showScheduleRemove = input<boolean>(true);
  permanentStartDate = input<string>();
  permanentEndDate = input<string>();
  isRulesDeletable = input<boolean>();
  readonly = input<boolean>();
  hasTransmissionCount = input<boolean>();
  maxTransmissionCount = input<number>();
  hasDefaultSkipCount = input<boolean>(false);
  allowEmptySchedule = input<boolean>(false);
  showOverrideWarning = input<boolean>();
  isNoCommunication = input<boolean>(false);
  isDisabledByEmptySchedule = input<boolean>(false);
  ignoreShortHeaderMessage = input<boolean>(false);
  showLoadFromTemplates = input<boolean>(false);
  showSaveAsTemplate = input<boolean>(true);
  showImportJson = input<boolean>(true);
  showLoadGpsXFile = input<boolean>(false);
  gpsXFileAccept = input<string>();
  warningMessages = input<string[]>();
  errorMessages = input<string[]>();
  hideRules = input<boolean>(false);
  showPreview = model<boolean>();
  initializeSequenceByAddSchedule = input<boolean>(false);

  scheduleService = computed<AbstractScheduleService>(() => {
    if (!this.scheduleType()) return undefined;
    return this.scheduleServices[this.scheduleType()];
  });

  errors = computed(() => {
    const serviceErrors = this.scheduleService()?.errors() ?? [];
    return this.hideRules()
      ? serviceErrors.filter((error) => error !== 'There is no rule')
      : serviceErrors;
  });

  private ruleSubscription: Subscription;
  private closeTooltipTimer: number | null = null;
  isTooltipOpen = signal(false);
  isLoadScheduleOpen = signal(false);
  loadScheduleTrigger = signal<CdkOverlayOrigin | undefined>(undefined);

  isSaveScheduleOpen = signal(false);
  saveScheduleTrigger = signal<CdkOverlayOrigin | undefined>(undefined);
  templateName = signal('');

  canExportJson = computed(
    () =>
      !!Object.values(this.scheduleService()?.rules?.() ?? {}).filter(
        (rule) => rule && Object.keys(rule).length > 0,
      ).length,
  );

  private importJsonInput = viewChild.required<ElementRef<HTMLInputElement>>('importJsonInput');
  private gpsXFileInput = viewChild.required<ElementRef<HTMLInputElement>>('gpsXFileInput');

  constructor() {
    effect(() => {
      this.remoteCommandsService.ignoreShortHeaderMessage$.next(this.ignoreShortHeaderMessage());
    });
  }

  ngOnInit() {
    this.ruleSubscription = this.scheduleService().rules$.subscribe(() =>
      this.scheduleService()?.checkValidity(),
    );
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  onClickAddRule() {
    this.scheduleService().onClickAddRule();
  }

  onRemoveRule(index: number) {
    this.scheduleService().onRemoveRule(index, this.readonly());
  }

  onClickRemove() {
    this.scheduleService().onRemoveSchedule();
    this.scheduleService().setToDefaultSchedule();
  }

  onClickRemoveEmpty() {
    if (this.allowEmptySchedule()) {
      this.scheduleService().setEmptySchedule();
    }
  }

  onAddSchedule() {
    if (this.allowEmptySchedule() && this.scheduleService().isEmptySchedule) {
      this.scheduleService().onRemoveSchedule();
    }
    this.scheduleService().isScheduleExpanded.set(true);
    this.scheduleService().isRuleExpanded[0] = true;

    if (this.initializeSequenceByAddSchedule()) {
      this.scheduleService().initializeRule(0, this.permanentStartDate(), this.permanentEndDate());
      this.scheduleService().addDefaultSequenceToRule(0);
    }
  }

  onLoadMenuClick(trigger: CdkOverlayOrigin) {
    this.loadScheduleTrigger.set(trigger);
  }

  onOpenTemplateSelector() {
    this.isLoadScheduleOpen.set(true);
  }

  onImportJson() {
    this.importJsonInput().nativeElement.click();
  }

  onLoadGpsXFile() {
    this.gpsXFileInput().nativeElement.click();
  }

  async onLoadGpsXFileSelected(event: Event) {
    await this.gpsXFileImportService.importScheduleFromEvent(event, this.scheduleType());
  }

  async onImportJsonSelected(event: Event) {
    const { schedule, errorMessage } = await importScheduleJsonFromInputEvent(
      event,
      this.scheduleType(),
    );

    if (errorMessage) {
      this.snackbarService.error(errorMessage);
      return;
    }

    if (!schedule) {
      return;
    }

    if (!schedule.rules?.length) {
      this.snackbarService.error('Imported schedule does not contain any rules.');
      return;
    }

    this.scheduleService().loadRules(schedule.rules);
    this.snackbarService.success(`Imported ${this.scheduleType()} schedule from JSON.`);
  }

  onTemplateSelected(template: dsDtos.ScheduleTemplateAdmin) {
    if (template.schedule?.rules) {
      this.scheduleService().loadRules(template.schedule.rules);
    }
    this.isLoadScheduleOpen.set(false);
  }

  onSaveMenuClick(trigger: CdkOverlayOrigin) {
    this.saveScheduleTrigger.set(trigger);
  }

  onOpenSaveTemplate() {
    this.isSaveScheduleOpen.set(true);
  }

  onConfirmSaveTemplate() {
    if (!this.templateName()) return;
    const template: dsDtos.AddScheduleTemplateCustomer = {
      schedule: this.scheduleService().toSchedule(),
      templateName: this.templateName(),
    };
    const templateName = this.templateName();
    this.templatesProvider?.add(template).subscribe(() => {
      this.isSaveScheduleOpen.set(false);
      this.templateName.set('');
      this.snackbarService.success(`Template "${templateName}" saved successfully`);
    });
  }

  onExportJson() {
    downloadScheduleJsonFile(this.scheduleService().toSchedule());
  }

  isDisabled() {
    if (this.readonly()) return true;
    if (this.isDisabledByScheduleResponse()) return true;
    if (this.isDisabledByEmptySchedule()) return true;
    return this.isMaxSchedulesReachedAndIsNotFilled();
  }

  isMaxSchedulesReachedAndIsNotFilled() {
    return (
      this.remoteCommandsService.isMaxSchedulesReached() &&
      !this.scheduleService().isFilledAndChanged()
    );
  }

  onClickConsult($event: MouseEvent) {
    $event.preventDefault();
    const url = 'https://www.vectronic-aerospace.com/editing-schedules/';
    const { desktop } = window;
    if (desktop) desktop.app.openUrlInDefaultBrowserTab(url);
    else window.open(url, '_blank', 'noopener');
  }

  onClickContact($event: MouseEvent) {
    $event.preventDefault();
    const url = 'https://www.vectronic-aerospace.com/support/';
    const { desktop } = window;
    if (desktop) desktop.app.openUrlInDefaultBrowserTab(url);
    else window.open(url, '_blank', 'noopener');
  }

  onTooltipMouseEnter() {
    if (this.closeTooltipTimer) {
      window.clearTimeout(this.closeTooltipTimer);
      this.closeTooltipTimer = null;
    }
    this.isTooltipOpen.set(true);
  }

  onTooltipMouseLeave() {
    this.closeTooltipTimer = window.setTimeout(() => {
      this.isTooltipOpen.set(false);
      this.closeTooltipTimer = null;
    }, 300);
  }

  ngOnDestroy() {
    this.ruleSubscription?.unsubscribe();
    if (this.closeTooltipTimer) {
      window.clearTimeout(this.closeTooltipTimer);
      this.closeTooltipTimer = null;
    }
  }
}
